import {CrosspointDataProviderErrorHandler} from "../types/crosspoint-data-provider-error.handler.interface";
import {ErrorRecoveryAction} from "../types/error-recovery-action.enum";

export class DefaultErrorHandler implements CrosspointDataProviderErrorHandler {
    /**
     * Handle fetch error and decide recovery action
     *
     * Algorithm:
     * 1. Check if error is AbortError → SKIP (user cancelled)
     * 2. Check if error is network-related → SKIP (transient)
     * 3. All other errors → ABORT (validation, logic errors)
     *
     * @param region - Region that failed to fetch
     * @param error - Error that occurred
     * @returns Recovery action (SKIP or ABORT)
     */
    onFetchError(_region: unknown, error: Error): ErrorRecoveryAction {
        // Log error for debugging
        console.error('[DataProvider] Fetch failed:', error);

        // AbortError: User cancelled (e.g., rapid scrolling)
        if (error.name === 'AbortError') {
            return ErrorRecoveryAction.SKIP;
        }

        // Network errors: Transient failures
        if (this.isNetworkError(error)) {
            return ErrorRecoveryAction.SKIP;
        }

        // All other errors: Validation, logic errors → ABORT
        return ErrorRecoveryAction.ABORT;
    }

    /**
     * Check if error is network-related
     *
     * Network errors include:
     * - "Failed to fetch" (Chrome)
     * - "NetworkError" (Firefox)
     * - "Network request failed" (general)
     *
     * @param error - Error to check
     * @returns True if network error
     *
     * @private
     */
    private isNetworkError(error: Error): boolean {
        const message = error.message.toLowerCase();
        return (
            message.includes('failed to fetch') ||
            message.includes('network') ||
            message.includes('timeout')
        );
    }
}
